import React from "react";
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  BooleanField,
  ReferenceField,
  ReferenceManyField,
  FunctionField,
  Datagrid,
  EditButton,
  Button,
  useTranslate,
  useRecordContext,
  BulkDeleteWithConfirmButton,
  TopToolbar,
  usePermissions,
  SimpleShowLayout,
} from "react-admin";
import { Link } from "react-router-dom";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Dns as DnsIcon,
} from "@mui/icons-material";

import GlobalPagination from "../common/GlobalPagination";
import { Domain } from "../types";
import { SYSTEM_ADMINISTRATOR } from "../authProvider/roles";

const buttonStyles = {
  marginTop: "1em",
  marginBottom: "1em",
};

const AddNewAliasDomainButton = () => {
  const record = useRecordContext<Domain>();
  return (
    <Button
      variant="contained"
      component={Link}
      to={{
        pathname: "/Domain/create",
        search: `?customerId=${record?.customerId}&mainDomainId=${record?.id}`,
      }}
      label="Alias-Domain hinzufügen"
      sx={buttonStyles}
    >
      <AddIcon />
    </Button>
  );
};

const AddNewMailAccountButton = () => {
  const record = useRecordContext<Domain>();
  return (
    <Button
      variant="contained"
      component={Link}
      to={{
        pathname: "/MailAccount/create",
        search: `?customerId=${record?.customerId}&domainId=${record?.id}&returnTo=MailAccountFromDomains`,
      }}
      label="Konto hinzufügen"
      sx={buttonStyles}
    >
      <AddIcon />
    </Button>
  );
};

const AddNewMailForwardButton = () => {
  const record = useRecordContext<Domain>();
  return (
    <Button
      variant="contained"
      component={Link}
      to={{
        pathname: "/MailForward/create",
        search: `?customerId=${record?.customerId}&domainId=${record?.id}&returnTo=MailForwardFromDomains`,
      }}
      label="Weiterleitung hinzufügen"
      sx={buttonStyles}
    >
      <AddIcon />
    </Button>
  );
};

const Title = () => {
  const record = useRecordContext<Domain>();
  return (
    <span>
      Domain <strong>{record?.unicodeName}</strong>
    </span>
  );
};

const EditMailForwardButton = () => {
  const record = useRecordContext<Domain>();
  return (
    <Button
      component={Link}
      to={{
        pathname: `/MailForward/${record?.id}`,
        search: `?returnTo=MailForwardFromDomains`,
      }}
      label="Bearbeiten"
      sx={buttonStyles}
    >
      <EditIcon />
    </Button>
  );
};

const EditMailAccountButton = () => {
  const record = useRecordContext<Domain>();
  return (
    <Button
      component={Link}
      to={{
        pathname: `/MailAccount/${record?.id}`,
        search: `?returnTo=MailAccountFromDomains`,
      }}
      label="Bearbeiten"
      sx={buttonStyles}
    >
      <EditIcon />
    </Button>
  );
};

const DomainShowLayout = () => {
  const translate = useTranslate();
  const record = useRecordContext<Domain>();
  const { permissions } = usePermissions();
  return (
    <TabbedShowLayout>
      <Tab label="Stammdaten">
        <TextField source="unicodeName" />
        <TextField source="domain" />
        <ReferenceField source="customerId" reference="Customer" link="show">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="mainDomainId" reference="Domain" link="show">
          <TextField source="domain" />
        </ReferenceField>
        <FunctionField<Domain>
          source="managedBy"
          render={(record) => {
            const value = record?.managedBy;
            return value
              ? translate(`resources.Domain.domainManager.${value}`)
              : "-";
          }}
        />
        <BooleanField source="mailEnabled" />
        <TextField source="username" />
      </Tab>
      {!record || record?.isMainDomain ? (
        <Tab label="Alias-Domains" path="alias-domains">
          <ReferenceManyField
            label={false}
            perPage={GlobalPagination.perPage}
            pagination={GlobalPagination.pagination}
            reference="Domain"
            target="mainDomainId"
            sort={{ field: "unicodeName", order: "ASC" }}
          >
            <Datagrid
              rowClick="show"
              bulkActionButtons={
                <BulkDeleteWithConfirmButton mutationMode="pessimistic" />
              }
            >
              <TextField source="unicodeName" />
              <BooleanField source="mailEnabled" />
              <EditButton />
            </Datagrid>
          </ReferenceManyField>
          <AddNewAliasDomainButton />
        </Tab>
      ) : null}
      <Tab label="E-Mail Konten" path="mail-accounts">
        <ReferenceManyField
          label={false}
          perPage={GlobalPagination.perPage}
          pagination={GlobalPagination.pagination}
          reference="MailAccount"
          target="domainId"
          sort={{ field: "emailAddress", order: "ASC" }}
        >
          <Datagrid
            bulkActionButtons={
              <BulkDeleteWithConfirmButton mutationMode="pessimistic" />
            }
          >
            <TextField source="emailAddress" />
            <TextField source="username" />
            <EditMailAccountButton />
          </Datagrid>
        </ReferenceManyField>
        <AddNewMailAccountButton />
      </Tab>
      <Tab label="E-Mail Weiterleitungen" path="mail-forwards">
        <ReferenceManyField
          label={false}
          perPage={GlobalPagination.perPage}
          pagination={GlobalPagination.pagination}
          reference="MailForward"
          target="domainId"
          sort={{ field: "fromEmailAddress", order: "ASC" }}
        >
          <Datagrid
            bulkActionButtons={
              <BulkDeleteWithConfirmButton mutationMode="pessimistic" />
            }
          >
            <TextField source="fromEmailAddress" />
            <TextField source="toEmailAddress" />
            <EditMailForwardButton />
          </Datagrid>
        </ReferenceManyField>
        <AddNewMailForwardButton />
      </Tab>
      {permissions === SYSTEM_ADMINISTRATOR && (
        <Tab label="Service-Buchung" path="service-booking">
          <ReferenceField
            source="serviceBookingId"
            reference="ServiceBooking"
            label={false}
          >
            <SimpleShowLayout>
              <TextField source="id" />
            </SimpleShowLayout>
          </ReferenceField>
          <ReferenceField
            source="serviceBookingId"
            reference="ServiceBooking"
            link={false}
            label={false}
          >
            <SimpleShowLayout>
              <TextField source="amount" />
              <ReferenceField
                source="serviceTemplateId"
                reference="ServiceTemplate"
              >
                <TextField source="name" />

              </ReferenceField>
              <TextField source="comment" />
            </SimpleShowLayout>
          </ReferenceField>
        </Tab>
      )}
    </TabbedShowLayout>
  );
};

const DomainShow = () => (
  <Show title={<Title />} actions={<DomainShowActions />}>
    <DomainShowLayout />
  </Show>
);

const DomainShowActions = () => {
  const record = useRecordContext();
  return (
    <TopToolbar>
      {record?.dnsZoneId && (
        <Button
          color="primary"
          component={Link}
          to={{
            pathname: `/DNSZone/${record?.dnsZoneId}/show`,
            search: `?domainId=${record?.id}`,
          }}
          label="DNS Zone"
          startIcon={<DnsIcon />}
        />
      )}
      <EditButton />
    </TopToolbar>
  );
};

export default DomainShow;
